import { useEffect, useRef, useState, type ReactNode } from "react";
import { navigate } from "./App";
import { useAuth } from "./auth";
import { api } from "./api";
import { Badge, Button, Empty, formatDisplayValue, Spinner, useResource } from "./components";
import { Icon, type IconName } from "./icons";
import type { JSONObject, ListResponse } from "./types";
import type { Subject } from "./types";
import { canAccessPath } from "./access";

interface NavItem { path: string; label: string; icon: IconName; section: string; permissionPath?: string }

const navigation: Record<string, NavItem[]> = {
  platform: [
    { path: "/dashboard", label: "首页", icon: "dashboard", section: "工作台" },
    { path: "/market", label: "采购市场", icon: "market", section: "采购管理" },
    { path: "/supply-demand", label: "需求与库存", icon: "activity", section: "采购管理", permissionPath: "/reports" },
    { path: "/inquiries", label: "采购询价", icon: "catalog", section: "采购管理", permissionPath: "/orders" },
    { path: "/orders", label: "采购订单", icon: "orders", section: "采购管理" },
    { path: "/automation", label: "自动采购策略", icon: "automation", section: "采购管理" },
    { path: "/integrations", label: "接收平台", icon: "integrations", section: "交付与数据" },
    { path: "/reports", label: "用量与对账", icon: "reports", section: "交付与数据" },
    { path: "/finance", label: "账户资金", icon: "finance", section: "资金管理" },
    { path: "/credit", label: "信用档案", icon: "security", section: "组织管理" },
    { path: "/support", label: "服务工单", icon: "support", section: "组织管理" },
    { path: "/security", label: "安全设置", icon: "security", section: "组织管理" },
  ],
  supplier: [
    { path: "/dashboard", label: "首页", icon: "dashboard", section: "工作台" },
    { path: "/inventory", label: "商品与 KEY", icon: "inventory", section: "商品管理" },
    { path: "/supply-demand", label: "市场需求", icon: "activity", section: "商品管理", permissionPath: "/reports" },
    { path: "/inquiries", label: "询价响应", icon: "catalog", section: "订单管理", permissionPath: "/orders" },
    { path: "/orders", label: "销售订单", icon: "orders", section: "订单管理" },
    { path: "/developer/imports", label: "批量上传与 API", icon: "developer", section: "接口与数据" },
    { path: "/reports", label: "消耗与对账", icon: "reports", section: "接口与数据" },
    { path: "/finance", label: "结算与提现", icon: "finance", section: "资金管理" },
    { path: "/credit", label: "信用档案", icon: "security", section: "组织管理" },
    { path: "/support", label: "服务工单", icon: "support", section: "组织管理" },
    { path: "/security", label: "安全设置", icon: "security", section: "组织管理" },
  ],
  operator: [
    { path: "/dashboard", label: "首页", icon: "dashboard", section: "工作台" },
    { path: "/operations/platforms", label: "客户与供应商", icon: "operations", section: "客户管理" },
    { path: "/supply-demand", label: "供需缺口", icon: "activity", section: "客户管理", permissionPath: "/reports" },
    { path: "/catalog/providers", label: "目录与报价", icon: "catalog", section: "商品治理", permissionPath: "/catalog-admin" },
    { path: "/finance", label: "资金对账与提成", icon: "finance", section: "资金管理" },
    { path: "/reports", label: "经营分析", icon: "reports", section: "数据中心" },
    { path: "/system", label: "系统管理", icon: "security", section: "平台治理" },
    { path: "/support", label: "工单中心", icon: "support", section: "平台治理" },
    { path: "/security", label: "安全审计", icon: "security", section: "平台治理" },
  ],
};

const systemNavigation: NavItem[] = [
  { path: "/system", label: "系统概览", icon: "dashboard", section: "系统总览" },
  { path: "/system/users", label: "用户管理", icon: "security", section: "账户与组织" },
  { path: "/system/organizations", label: "组织管理", icon: "operations", section: "账户与组织" },
  { path: "/system/operators", label: "运营权限", icon: "security", section: "账户与组织" },
  { path: "/system/payments", label: "支付渠道", icon: "finance", section: "基础配置" },
  { path: "/system/mail", label: "邮件通知", icon: "support", section: "基础配置" },
  { path: "/system/audit", label: "审计日志", icon: "reports", section: "治理与运行" },
  { path: "/system/health", label: "系统状态", icon: "activity", section: "治理与运行" },
];

function systemActionNavigation(pathname: string): NavItem[] {
  const userStatus = /^\/system\/users\/[^/]+\/status$/.test(pathname);
  const organizationStatus = /^\/system\/organizations\/[^/]+\/status$/.test(pathname);
  const organizationCredit = /^\/system\/organizations\/[^/]+\/credit$/.test(pathname);
  const operatorAction = pathname === "/system/operators/new" || /^\/system\/operators\/[^/]+\/edit$/.test(pathname);
  const returnPath = userStatus ? "/system/users"
    : organizationStatus || organizationCredit ? "/system/organizations"
    : "/system/operators";
  const returnLabel = userStatus ? "返回用户管理"
    : organizationStatus || organizationCredit ? "返回组织管理"
    : "返回运营权限";
  const actionLabel = userStatus ? "调整用户状态"
    : organizationStatus ? "调整组织状态"
    : organizationCredit ? "组织信用档案"
    : pathname === "/system/operators/new" ? "新增运营成员"
    : "编辑运营成员";
  return [
    { path: returnPath, label: returnLabel, icon: operatorAction ? "security" : organizationCredit || organizationStatus ? "operations" : "security", section: "系统管理", permissionPath: "/system" },
    { path: pathname, label: actionLabel, icon: organizationCredit ? "reports" : "security", section: "当前操作", permissionPath: "/system" },
  ];
}

const operationsNavigation: NavItem[] = [
  { path: "/operations/platforms", label: "平台方管理", icon: "operations", section: "客户经营" },
  { path: "/operations/suppliers", label: "供应商管理", icon: "inventory", section: "客户经营" },
  { path: "/operations/managed-platforms", label: "接收台与自动推送", icon: "integrations", section: "客户经营" },
  { path: "/operations/assignments", label: "客户分配", icon: "security", section: "客户经营" },
  { path: "/operations/inventory", label: "全局库存治理", icon: "inventory", section: "供货治理" },
  { path: "/operations/targets", label: "接收实例管理", icon: "integrations", section: "接入治理" },
  { path: "/operations/hosted-newapi", label: "托管 NewAPI 租户", icon: "security", section: "接入治理" },
  { path: "/operations/deliveries", label: "订单与交付", icon: "orders", section: "履约管理" },
  { path: "/operations/reviews", label: "审核队列", icon: "catalog", section: "履约管理" },
  { path: "/operations/team", label: "运营团队", icon: "security", section: "团队治理" },
  { path: "/operations/audit", label: "业务审计", icon: "reports", section: "团队治理" },
];

function operationsActionNavigation(pathname: string): NavItem[] {
  if (pathname === "/operations/platforms/new") {
    return [
      { path: "/operations/platforms", label: "返回平台方管理", icon: "operations", section: "导航", permissionPath: "/operations/platforms" },
      { path: pathname, label: "新增平台用户", icon: "operations", section: "当前操作", permissionPath: "/operations/platforms" },
    ];
  }
  if (pathname === "/operations/suppliers/new") {
    return [
      { path: "/operations/suppliers", label: "返回供应商管理", icon: "inventory", section: "导航", permissionPath: "/operations/suppliers" },
      { path: pathname, label: "新增供应商账号", icon: "inventory", section: "当前操作", permissionPath: "/operations/suppliers" },
      { path: "/operations/reviews", label: "供应商资质审核", icon: "catalog", section: "关联业务", permissionPath: "/operations/reviews" },
    ];
  }
  if (pathname.startsWith("/operations/managed-platforms/")) {
    return [
      { path: "/operations/managed-platforms", label: "返回接收台列表", icon: "integrations", section: "导航", permissionPath: "/operations/managed-platforms" },
      { path: "/operations/managed-platforms/new", label: "新增并测试渠道", icon: "integrations", section: "平台接入", permissionPath: "/operations/managed-platforms" },
      { path: "/operations/platforms", label: "平台方管理", icon: "operations", section: "关联业务", permissionPath: "/operations/platforms" },
      { path: "/operations/assignments", label: "客户分配", icon: "security", section: "关联业务", permissionPath: "/operations/assignments" },
    ];
  }
  if (pathname.startsWith("/operations/assignments/")) {
    return [
      { path: "/operations/assignments", label: "返回客户分配", icon: "security", section: "导航", permissionPath: "/operations/assignments" },
      { path: "/operations/assignments/new", label: "新增客户分配", icon: "security", section: "客户归属", permissionPath: "/operations/assignments" },
      { path: "/operations/team", label: "运营团队", icon: "security", section: "关联配置", permissionPath: "/operations/team" },
      { path: "/operations/platforms", label: "平台方管理", icon: "operations", section: "关联配置", permissionPath: "/operations/platforms" },
      { path: "/operations/suppliers", label: "供应商管理", icon: "inventory", section: "关联配置", permissionPath: "/operations/suppliers" },
    ];
  }
  if (pathname.startsWith("/operations/team/")) {
    const memberMatch = pathname.match(/^\/operations\/team\/([^/]+)\/edit$/);
    return [
      { path: "/operations/team", label: "返回运营团队", icon: "security", section: "导航", permissionPath: "/operations/team" },
      { path: "/operations/team/new", label: "新增运营成员", icon: "security", section: "团队治理", permissionPath: "/operations/team" },
      ...(memberMatch?.[1] ? [{ path: pathname, label: "编辑成员权限", icon: "security", section: "团队治理", permissionPath: "/operations/team" } satisfies NavItem] : []),
      { path: "/operations/assignments", label: "客户分配", icon: "security", section: "关联业务", permissionPath: "/operations/assignments" },
      { path: "/operations/audit", label: "业务审计", icon: "reports", section: "关联业务", permissionPath: "/operations/audit" },
    ];
  }
  const reviewMatch = pathname.match(/^\/operations\/reviews\/([^/]+)\/([^/]+)$/);
  return [
    { path: "/operations/reviews", label: "返回审核队列", icon: "catalog", section: "导航", permissionPath: "/operations/reviews" },
    { path: "/operations/reviews/new", label: "按编号审核", icon: "catalog", section: "审核工作区", permissionPath: "/operations/reviews" },
    ...(reviewMatch ? [{ path: pathname, label: "审核业务对象", icon: "catalog", section: "审核工作区", permissionPath: "/operations/reviews" } satisfies NavItem] : []),
    { path: "/operations/audit", label: "业务审计", icon: "reports", section: "关联业务", permissionPath: "/operations/audit" },
  ];
}

const catalogNavigation: NavItem[] = [
  { path: "/catalog/providers", label: "供应平台", icon: "catalog", section: "目录管理", permissionPath: "/catalog-admin" },
  { path: "/catalog/credential-types", label: "KEY 类型", icon: "security", section: "目录管理", permissionPath: "/catalog-admin" },
  { path: "/catalog/models", label: "模型目录", icon: "inventory", section: "目录管理", permissionPath: "/catalog-admin" },
  { path: "/catalog/pricing", label: "价格规则", icon: "finance", section: "平台盈利", permissionPath: "/catalog-admin" },
];

function catalogActionNavigation(pathname: string): NavItem[] {
  if (pathname === "/catalog/providers/new") {
    return [
      { path: "/catalog/providers", label: "返回供应平台", icon: "catalog", section: "导航", permissionPath: "/catalog-admin" },
      { path: pathname, label: "新增供应平台", icon: "catalog", section: "目录操作", permissionPath: "/catalog-admin" },
      { path: "/catalog/credential-types", label: "KEY 类型", icon: "security", section: "关联目录", permissionPath: "/catalog-admin" },
      { path: "/catalog/models", label: "模型目录", icon: "inventory", section: "关联目录", permissionPath: "/catalog-admin" },
    ];
  }
  if (pathname === "/catalog/credential-types/new") {
    return [
      { path: "/catalog/credential-types", label: "返回 KEY 类型", icon: "security", section: "导航", permissionPath: "/catalog-admin" },
      { path: pathname, label: "新增 KEY 类型", icon: "security", section: "目录操作", permissionPath: "/catalog-admin" },
      { path: "/catalog/providers", label: "供应平台", icon: "catalog", section: "关联目录", permissionPath: "/catalog-admin" },
      { path: "/catalog/models", label: "模型目录", icon: "inventory", section: "关联目录", permissionPath: "/catalog-admin" },
    ];
  }
  if (pathname === "/catalog/models/new") {
    return [
      { path: "/catalog/models", label: "返回模型目录", icon: "inventory", section: "导航", permissionPath: "/catalog-admin" },
      { path: pathname, label: "新增模型", icon: "inventory", section: "目录操作", permissionPath: "/catalog-admin" },
      { path: "/catalog/providers", label: "供应平台", icon: "catalog", section: "关联目录", permissionPath: "/catalog-admin" },
      { path: "/catalog/credential-types", label: "KEY 类型", icon: "security", section: "关联目录", permissionPath: "/catalog-admin" },
    ];
  }
  return [
    { path: "/catalog/pricing", label: "返回价格规则", icon: "finance", section: "导航", permissionPath: "/catalog-admin" },
    { path: pathname, label: "发布价格规则", icon: "finance", section: "定价操作", permissionPath: "/catalog-admin" },
    { path: "/catalog/providers", label: "供应平台", icon: "catalog", section: "关联目录", permissionPath: "/catalog-admin" },
    { path: "/operations/audit", label: "业务审计", icon: "reports", section: "关联治理", permissionPath: "/operations/audit" },
  ];
}

const financeNavigation: Record<"platform" | "supplier" | "operator", NavItem[]> = {
  platform: [
    { path: "/finance", label: "资金总览", icon: "finance", section: "账户资金" },
    { path: "/finance/topups", label: "充值管理", icon: "finance", section: "账户资金" },
    { path: "/finance/activity", label: "资金流水", icon: "reports", section: "账户资金" },
    { path: "/finance/refunds", label: "充值退款", icon: "orders", section: "资金服务" },
    { path: "/finance/invoices", label: "发票与对公", icon: "support", section: "资金服务" },
  ],
  supplier: [
    { path: "/finance", label: "资金总览", icon: "finance", section: "结算中心" },
    { path: "/finance/settlements", label: "结算明细", icon: "reports", section: "结算中心" },
    { path: "/finance/withdrawals", label: "提现管理", icon: "finance", section: "结算中心" },
    { path: "/finance/accounts", label: "收款账户", icon: "security", section: "结算中心" },
    { path: "/finance/activity", label: "资金流水", icon: "reports", section: "账务记录" },
  ],
  operator: [
    { path: "/finance", label: "财务总览", icon: "finance", section: "资金管理" },
    { path: "/finance/balances", label: "客户余额", icon: "operations", section: "资金管理" },
    { path: "/finance/adjustments", label: "余额调整", icon: "finance", section: "资金管理" },
    { path: "/finance/refunds", label: "充值退款", icon: "orders", section: "资金审核" },
    { path: "/finance/withdrawals", label: "供应商提现", icon: "finance", section: "资金审核" },
    { path: "/finance/commissions", label: "运营提成", icon: "reports", section: "财务运营" },
    { path: "/finance/platform-revenue", label: "平台利润明细", icon: "finance", section: "财务运营" },
    { path: "/finance/invoices", label: "发票与对公", icon: "support", section: "财务运营" },
  ],
};

function financeActionNavigation(pathname: string, organizationType: "platform" | "supplier" | "operator"): NavItem[] {
  if (pathname === "/finance/topups/new" && organizationType === "platform") {
    return [
      { path: "/finance/topups", label: "返回充值管理", icon: "finance", section: "导航", permissionPath: "/finance/topups" },
      { path: pathname, label: "发起充值", icon: "finance", section: "充值操作", permissionPath: "/finance/topups" },
      { path: "/finance/activity", label: "资金流水", icon: "reports", section: "关联记录", permissionPath: "/finance/activity" },
      { path: "/finance/refunds", label: "充值退款", icon: "orders", section: "关联记录", permissionPath: "/finance/refunds" },
    ];
  }
  if (pathname === "/finance/accounts/new" && organizationType === "supplier") {
    return [
      { path: "/finance/accounts", label: "返回收款账户", icon: "security", section: "导航", permissionPath: "/finance/accounts" },
      { path: pathname, label: "新增收款账户", icon: "security", section: "账户操作", permissionPath: "/finance/accounts" },
      { path: "/finance/withdrawals", label: "提现管理", icon: "finance", section: "关联业务", permissionPath: "/finance/withdrawals" },
      { path: "/finance/settlements", label: "结算明细", icon: "reports", section: "关联业务", permissionPath: "/finance/settlements" },
    ];
  }
  if (pathname === "/finance/withdrawals/new" && organizationType === "supplier") {
    return [
      { path: "/finance/withdrawals", label: "返回提现管理", icon: "finance", section: "导航", permissionPath: "/finance/withdrawals" },
      { path: pathname, label: "申请提现", icon: "finance", section: "提现操作", permissionPath: "/finance/withdrawals" },
      { path: "/finance/accounts", label: "收款账户", icon: "security", section: "关联配置", permissionPath: "/finance/accounts" },
      { path: "/finance/settlements", label: "结算明细", icon: "reports", section: "关联配置", permissionPath: "/finance/settlements" },
    ];
  }
  if (pathname === "/finance/adjustments/new" && organizationType === "operator") {
    return [
      { path: "/finance/adjustments", label: "返回余额调整", icon: "finance", section: "导航", permissionPath: "/finance/adjustments" },
      { path: pathname, label: "发起余额调整", icon: "finance", section: "账务操作", permissionPath: "/finance/adjustments" },
      { path: "/finance/balances", label: "客户余额", icon: "operations", section: "关联业务", permissionPath: "/finance/balances" },
      { path: "/finance/commissions", label: "运营提成", icon: "reports", section: "关联业务", permissionPath: "/finance/commissions" },
    ];
  }
  if (pathname === "/finance/commissions/new" && organizationType === "operator") {
    return [
      { path: "/finance/commissions", label: "返回运营提成", icon: "reports", section: "导航", permissionPath: "/finance/commissions" },
      { path: pathname, label: "新建提成规则", icon: "reports", section: "规则配置", permissionPath: "/finance/commissions" },
      { path: "/operations/assignments", label: "客户分配", icon: "security", section: "关联配置", permissionPath: "/operations/assignments" },
      { path: "/finance/balances", label: "客户余额", icon: "operations", section: "关联配置", permissionPath: "/finance/balances" },
    ];
  }
  return [
    { path: "/finance/invoices", label: "返回发票与对公", icon: "support", section: "导航", permissionPath: "/finance/invoices" },
    { path: pathname, label: "申请开票", icon: "support", section: "开票操作", permissionPath: "/finance/invoices" },
    { path: "/finance", label: "资金总览", icon: "finance", section: "关联账务", permissionPath: "/finance" },
    { path: "/finance/activity", label: "资金流水", icon: "reports", section: "关联账务", permissionPath: "/finance/activity" },
  ];
}

function financeProcessNavigation(pathname: string): NavItem[] {
  if (/^\/finance\/topups\/[^/]+\/cancel$/.test(pathname)) {
    return [
      { path: "/finance/topups", label: "返回充值管理", icon: "finance", section: "导航", permissionPath: "/finance/topups" },
      { path: pathname, label: "作废充值单", icon: "finance", section: "充值操作", permissionPath: "/finance/topups" },
      { path: "/finance/activity", label: "资金流水", icon: "reports", section: "关联记录", permissionPath: "/finance/activity" },
    ];
  }
  if (/^\/finance\/topups\/[^/]+\/refund$/.test(pathname)) {
    return [
      { path: "/finance/topups", label: "返回充值管理", icon: "finance", section: "导航", permissionPath: "/finance/topups" },
      { path: pathname, label: "申请充值退款", icon: "orders", section: "退款处理", permissionPath: "/finance/topups" },
      { path: "/finance/refunds", label: "退款记录", icon: "orders", section: "关联账务", permissionPath: "/finance/refunds" },
      { path: "/finance/activity", label: "资金流水", icon: "reports", section: "关联账务", permissionPath: "/finance/activity" },
    ];
  }
  const refundMatch = pathname.match(/^\/finance\/refunds\/[^/]+\/(complete|reject)$/);
  if (refundMatch) {
    return [
      { path: "/finance/refunds", label: "返回退款复核", icon: "orders", section: "导航", permissionPath: "/finance/refunds" },
      { path: pathname, label: refundMatch[1] === "complete" ? "确认充值退款" : "驳回充值退款", icon: "orders", section: "退款复核", permissionPath: "/finance/refunds" },
      { path: "/finance/adjustments", label: "余额调整", icon: "finance", section: "关联审核", permissionPath: "/finance/adjustments" },
      { path: "/operations/audit", label: "业务审计", icon: "reports", section: "关联审核", permissionPath: "/operations/audit" },
    ];
  }
  const adjustmentMatch = pathname.match(/^\/finance\/adjustments\/[^/]+\/(approve|reject)$/);
  if (adjustmentMatch) {
    return [
      { path: "/finance/adjustments", label: "返回余额调整", icon: "finance", section: "导航", permissionPath: "/finance/adjustments" },
      { path: pathname, label: adjustmentMatch[1] === "approve" ? "通过余额调整" : "驳回余额调整", icon: "finance", section: "余额复核", permissionPath: "/finance/adjustments" },
      { path: "/finance/balances", label: "客户余额", icon: "operations", section: "关联账务", permissionPath: "/finance/balances" },
      { path: "/operations/audit", label: "业务审计", icon: "reports", section: "关联账务", permissionPath: "/operations/audit" },
    ];
  }
  if (/^\/finance\/withdrawals\/[^/]+\/payout$/.test(pathname)) {
    return [
      { path: "/finance/withdrawals", label: "返回供应商提现", icon: "finance", section: "导航", permissionPath: "/finance/withdrawals" },
      { path: pathname, label: "确认供应商打款", icon: "finance", section: "提现打款", permissionPath: "/finance/withdrawals" },
      { path: "/finance/balances", label: "客户余额", icon: "operations", section: "关联账务", permissionPath: "/finance/balances" },
      { path: "/operations/audit", label: "业务审计", icon: "reports", section: "关联账务", permissionPath: "/operations/audit" },
    ];
  }
  if (/^\/finance\/commissions\/[^/]+\/payout$/.test(pathname)) {
    return [
      { path: "/finance/commissions", label: "返回运营提成", icon: "reports", section: "导航", permissionPath: "/finance/commissions" },
      { path: pathname, label: "确认运营提成结算", icon: "finance", section: "提成结算", permissionPath: "/finance/commissions" },
      { path: "/finance/platform-revenue", label: "平台利润明细", icon: "finance", section: "关联账务", permissionPath: "/finance/platform-revenue" },
      { path: "/operations/audit", label: "业务审计", icon: "reports", section: "关联账务", permissionPath: "/operations/audit" },
    ];
  }
  return [
    { path: "/finance/invoices", label: "返回发票与对公", icon: "support", section: "导航", permissionPath: "/finance/invoices" },
    { path: pathname, label: "处理发票申请", icon: "support", section: "开票处理", permissionPath: "/finance/invoices" },
    { path: "/operations/audit", label: "业务审计", icon: "reports", section: "关联记录", permissionPath: "/operations/audit" },
  ];
}

const developerNavigation: NavItem[] = [
  { path: "/developer/imports", label: "批量任务", icon: "developer", section: "开放接口" },
  { path: "/developer/clients", label: "API 客户端", icon: "security", section: "开放接口" },
  { path: "/developer/webhooks", label: "Webhook 端点", icon: "activity", section: "事件订阅" },
  { path: "/developer/webhook-deliveries", label: "投递记录", icon: "reports", section: "事件订阅" },
  { path: "/developer/proofs", label: "能力证明", icon: "reports", section: "资质与审核" },
];

function developerActionNavigation(pathname: string): NavItem[] {
  if (pathname === "/developer/imports/new") {
    return [
      { path: "/developer/imports", label: "返回批量任务", icon: "developer", section: "导航", permissionPath: "/developer/imports" },
      { path: pathname, label: "新建异步导入", icon: "developer", section: "批量入库", permissionPath: "/developer/imports" },
      { path: "/inventory", label: "商品与 KEY", icon: "inventory", section: "关联业务", permissionPath: "/inventory" },
      { path: "/developer/clients", label: "API 客户端", icon: "security", section: "关联接口", permissionPath: "/developer/clients" },
    ];
  }
  if (pathname === "/developer/clients/new") {
    return [
      { path: "/developer/clients", label: "返回 API 客户端", icon: "security", section: "导航", permissionPath: "/developer/clients" },
      { path: pathname, label: "创建 API 客户端", icon: "security", section: "接口凭证", permissionPath: "/developer/clients" },
      { path: "/developer/imports", label: "批量任务", icon: "developer", section: "关联接口", permissionPath: "/developer/imports" },
      { path: "/developer/webhooks", label: "Webhook 端点", icon: "activity", section: "关联接口", permissionPath: "/developer/webhooks" },
    ];
  }
  return [
    { path: "/developer/webhooks", label: "返回 Webhook 端点", icon: "activity", section: "导航", permissionPath: "/developer/webhooks" },
    { path: pathname, label: "添加 Webhook", icon: "activity", section: "事件订阅", permissionPath: "/developer/webhooks" },
    { path: "/developer/webhook-deliveries", label: "投递记录", icon: "reports", section: "关联记录", permissionPath: "/developer/webhook-deliveries" },
    { path: "/developer/clients", label: "API 客户端", icon: "security", section: "关联接口", permissionPath: "/developer/clients" },
  ];
}

const reportsNavigation: Record<"platform" | "supplier" | "operator", NavItem[]> = {
  platform: [
    { path: "/reports", label: "用量概览", icon: "reports", section: "用量分析", permissionPath: "/reports" },
    { path: "/reports/usage", label: "用量明细", icon: "orders", section: "用量分析", permissionPath: "/reports" },
    { path: "/reports/collection-health", label: "采集健康", icon: "integrations", section: "数据质量", permissionPath: "/reports" },
    { path: "/reports/reconciliations", label: "账单核对", icon: "finance", section: "账务核对", permissionPath: "/reports" },
  ],
  supplier: [
    { path: "/reports", label: "消耗概览", icon: "reports", section: "已售用量", permissionPath: "/reports" },
    { path: "/reports/usage", label: "用量明细", icon: "orders", section: "已售用量", permissionPath: "/reports" },
    { path: "/reports/collection-health", label: "采集健康", icon: "integrations", section: "数据质量", permissionPath: "/reports" },
    { path: "/reports/reconciliations", label: "账单核对", icon: "finance", section: "结算核对", permissionPath: "/reports" },
  ],
  operator: [
    { path: "/reports", label: "经营概览", icon: "reports", section: "经营分析", permissionPath: "/reports" },
    { path: "/reports/collection-health", label: "采集健康", icon: "integrations", section: "数据质量", permissionPath: "/reports" },
    { path: "/reports/reconciliations", label: "账单核对", icon: "finance", section: "账务核对", permissionPath: "/reports" },
  ],
};

function securityNavigation(subject: Subject): NavItem[] {
  const canManageMembers = subject.roles.some((role) => ["admin", "platform_owner", "supplier_owner"].includes(role));
  const canManageBlacklists = subject.roles.some((role) => ["platform_owner", "supplier_owner"].includes(role));
  const items: NavItem[] = [
    { path: "/security", label: "账户安全", icon: "security", section: "账号安全", permissionPath: "/security" },
    { path: "/security/sessions", label: "活跃会话", icon: "activity", section: "账号安全", permissionPath: "/security" },
  ];
  if (canManageMembers) items.push(
    { path: "/security/members", label: "组织成员", icon: "operations", section: "组织权限", permissionPath: "/security" },
    { path: "/security/invitations", label: "成员邀请", icon: "support", section: "组织权限", permissionPath: "/security" },
  );
  if (canManageBlacklists) items.push(
    { path: "/security/blacklists", label: "交易黑名单", icon: "orders", section: "交易限制", permissionPath: "/security" },
  );
  return items;
}

function securityActionNavigation(pathname: string, subject: Subject): NavItem[] {
  const isInvitation = pathname === "/security/invitations/new";
  const isMember = /^\/security\/members\/[^/]+\/edit$/.test(pathname);
  const actionLabel = pathname === "/security/password" ? "修改登录密码"
    : pathname === "/security/mfa/setup" ? "设置验证器 MFA"
    : pathname === "/security/mfa/disable" ? "停用验证器 MFA"
    : isInvitation ? "邀请组织成员"
    : "管理成员权限";
  const returnPath = isInvitation ? "/security/invitations" : isMember ? "/security/members" : "/security";
  const returnLabel = isInvitation ? "返回成员邀请" : isMember ? "返回组织成员" : "返回账户安全";
  return [
    { path: returnPath, label: returnLabel, icon: isInvitation ? "support" : isMember ? "operations" : "security", section: "安全中心", permissionPath: "/security" },
    { path: pathname, label: actionLabel, icon: "security", section: "当前操作", permissionPath: "/security" },
  ];
}

function primaryNavigation(subject: Subject): NavItem[] {
  const canManageInquiries = subject.roles.some((role) => subject.organization_type === "platform"
    ? role === "platform_owner"
    : role === "supplier_owner" || role === "supplier_operator");
  return (navigation[subject.organization_type] ?? []).filter((item) => item.path !== "/inquiries" || canManageInquiries);
}

function supplierListingNavigation(pathname: string): NavItem[] {
  if (pathname === "/inventory/new") {
    return [
      { path: "/inventory", label: "返回商品列表", icon: "inventory", section: "导航", permissionPath: "/inventory" },
      { path: "/inventory/new", label: "新建商品", icon: "catalog", section: "商品创建", permissionPath: "/inventory" },
    ];
  }
  const credentialEditMatch = pathname.match(/^\/inventory\/([^/]+)\/keys\/([^/]+)\/edit$/);
  if (credentialEditMatch?.[1] && credentialEditMatch[2]) {
    const inventoryPath = `/inventory/${credentialEditMatch[1]}/keys`;
    return [
      { path: "/inventory", label: "返回商品列表", icon: "inventory", section: "导航", permissionPath: "/inventory" },
      { path: inventoryPath, label: "返回 KEY 库存", icon: "inventory", section: "导航", permissionPath: "/inventory" },
      { path: pathname, label: "修改 KEY 参数", icon: "catalog", section: "KEY 管理", permissionPath: "/inventory" },
    ];
  }
  const match = pathname.match(/^\/inventory\/([^/]+)/);
  if (!match) return navigation.supplier ?? [];
  const id = match[1];
  const base = `/inventory/${id}`;
  return [
    { path: "/inventory", label: "返回商品列表", icon: "inventory", section: "导航", permissionPath: "/inventory" },
    { path: base, label: "商品概览", icon: "dashboard", section: "商品工作区", permissionPath: "/inventory" },
    { path: `${base}/keys`, label: "KEY 库存", icon: "inventory", section: "库存管理", permissionPath: "/inventory" },
    { path: `${base}/upload`, label: "批量上传", icon: "developer", section: "库存管理", permissionPath: "/inventory" },
    { path: `${base}/edit`, label: "商品设置", icon: "catalog", section: "商品管理", permissionPath: "/inventory" },
    { path: `${base}/quote`, label: "报价版本", icon: "finance", section: "商品管理", permissionPath: "/inventory" },
    { path: `${base}/rpm`, label: "RPM 策略", icon: "activity", section: "质量与审核", permissionPath: "/inventory" },
    { path: `${base}/evidence`, label: "证明材料", icon: "reports", section: "质量与审核", permissionPath: "/inventory" },
    { path: `${base}/state`, label: "上架与审核", icon: "security", section: "质量与审核", permissionPath: "/inventory" },
  ];
}

function customerProfileNavigation(pathname: string): NavItem[] {
  const match = pathname.match(/^\/operations\/(platforms|suppliers)\/([^/]+)$/);
  if (!match?.[1] || !match[2]) return operationsNavigation;
  const kind = match[1];
  const organizationID = decodeURIComponent(match[2]);
  const customerType = kind === "suppliers" ? "supplier" : "platform";
  return [
    { path: `/operations/${kind}`, label: `返回${customerType === "supplier" ? "供应商" : "平台方"}列表`, icon: customerType === "supplier" ? "inventory" : "operations", section: "导航", permissionPath: `/operations/${kind}` },
    { path: pathname, label: "客户档案", icon: "dashboard", section: "客户工作区", permissionPath: `/operations/${kind}` },
    { path: `/operations/deliveries?customer_id=${encodeURIComponent(organizationID)}`, label: "履约与订单", icon: "orders", section: "客户工作区", permissionPath: "/operations/deliveries" },
    { path: `/finance/balances?type=${customerType}&query=${encodeURIComponent(organizationID)}`, label: "余额与调账", icon: "finance", section: "客户工作区", permissionPath: "/finance/balances" },
    { path: `/reports/reconciliations?organization_id=${encodeURIComponent(organizationID)}`, label: "账单核对", icon: "reports", section: "客户工作区", permissionPath: "/reports" },
  ];
}

function reconciliationDetailNavigation(pathname: string): NavItem[] {
  return [
    { path: "/reports/reconciliations", label: "返回对账列表", icon: "reports", section: "导航", permissionPath: "/reports" },
    { path: pathname, label: "核对批次详情", icon: "dashboard", section: "账单核对", permissionPath: "/reports" },
  ];
}

function reconciliationActionNavigation(pathname: string): NavItem[] {
  if (pathname === "/reports/reconciliations/new") {
    return [
      { path: "/reports/reconciliations", label: "返回核对列表", icon: "reports", section: "导航", permissionPath: "/reports" },
      { path: pathname, label: "创建核对批次", icon: "dashboard", section: "账单核对", permissionPath: "/reports" },
    ];
  }
  const batchID = pathname.match(/^\/reports\/reconciliations\/([^/]+)\/differences\//)?.[1] ?? "";
  return [
    { path: `/reports/reconciliations/${batchID}`, label: "返回核对批次", icon: "reports", section: "导航", permissionPath: "/reports" },
    { path: pathname, label: "处理账单差异", icon: "dashboard", section: "账单核对", permissionPath: "/reports" },
  ];
}

function orderDetailNavigation(pathname: string, organizationType: string, roles: string[]): NavItem[] {
  const orderMatch = pathname.match(/^\/orders\/([^/]+)/);
  const detailPath = orderMatch?.[1] ? `/orders/${orderMatch[1]}` : pathname;
  const items: NavItem[] = [
    { path: "/orders", label: organizationType === "supplier" ? "返回销售订单" : "返回采购订单", icon: "orders", section: "导航", permissionPath: "/orders" },
    { path: detailPath, label: "订单详情", icon: "dashboard", section: "订单工作区", permissionPath: "/orders" },
  ];
  const actionLabel = pathname.endsWith("/push") ? "绑定目标并推送"
    : pathname.endsWith("/refund") ? "订单全额退款"
    : pathname.endsWith("/extract") ? "一次性提取 KEY"
    : pathname.endsWith("/block-platform") ? "拉黑匿名平台"
    : "";
  if (actionLabel) {
    items.push({ path: pathname, label: actionLabel, icon: "security", section: "当前操作", permissionPath: "/orders" });
  }
  if (organizationType === "platform" && roles.includes("platform_owner")) {
    items.push({ path: `${detailPath}/deliveries`, label: "交付任务", icon: "orders", section: "订单工作区", permissionPath: "/orders" });
  }
  items.push(
    { path: "/reports", label: organizationType === "supplier" ? "消耗与对账" : "用量与对账", icon: "reports", section: "履约数据", permissionPath: "/reports" },
    { path: "/finance", label: organizationType === "supplier" ? "结算与提现" : "账户资金", icon: "finance", section: "履约数据", permissionPath: "/finance" },
  );
  return items;
}

function inquiryActionNavigation(pathname: string, organizationType: string): NavItem[] {
  const actionLabel = pathname.endsWith("/respond") ? "响应询价" : "按锁价购买";
  return [
    { path: "/inquiries", label: "返回询价列表", icon: "orders", section: "导航", permissionPath: "/orders" },
    { path: pathname, label: actionLabel, icon: organizationType === "supplier" ? "finance" : "orders", section: "询价处理", permissionPath: "/orders" },
  ];
}

function marketplaceListingNavigation(pathname: string): NavItem[] {
  const listingMatch = pathname.match(/^\/market\/([^/]+)/);
  const detailPath = listingMatch?.[1] ? `/market/${listingMatch[1]}` : pathname;
  const items: NavItem[] = [
    { path: "/market", label: "返回采购市场", icon: "market", section: "导航", permissionPath: "/market" },
    { path: detailPath, label: "商品详情", icon: "dashboard", section: "采购工作区", permissionPath: "/market" },
    { path: `${detailPath}/purchase`, label: "购买确认", icon: "orders", section: "采购工作区", permissionPath: "/market" },
    { path: "/orders", label: "采购订单", icon: "orders", section: "采购记录", permissionPath: "/orders" },
    { path: "/finance", label: "账户资金", icon: "finance", section: "采购记录", permissionPath: "/finance" },
  ];
  if (pathname.endsWith("/block")) {
    items.splice(3, 0, { path: pathname, label: "拉黑匿名供应商", icon: "security", section: "交易隔离", permissionPath: "/market" });
  }
  return items;
}

function creditActionNavigation(): NavItem[] {
  return [
    { path: "/credit", label: "返回信用档案", icon: "security", section: "导航", permissionPath: "/credit" },
    { path: "/credit/appeal", label: "发起信用申诉", icon: "support", section: "信用申诉", permissionPath: "/credit" },
  ];
}

function operatorDeliveryDetailNavigation(pathname: string): NavItem[] {
  return [
    { path: "/operations/deliveries", label: "返回订单与交付", icon: "orders", section: "导航", permissionPath: "/operations/deliveries" },
    { path: pathname, label: "履约详情", icon: "dashboard", section: "履约工作区", permissionPath: "/operations/deliveries" },
    { path: "/reports", label: "经营与对账报表", icon: "reports", section: "履约核对", permissionPath: "/reports" },
    { path: "/operations/audit", label: "业务审计", icon: "security", section: "履约核对", permissionPath: "/operations/audit" },
  ];
}

function ticketDetailNavigation(pathname: string, organizationType: string): NavItem[] {
  return [
    { path: "/support", label: organizationType === "operator" ? "返回工单中心" : "返回工单列表", icon: "support", section: "导航", permissionPath: "/support" },
    { path: pathname, label: "工单详情", icon: "dashboard", section: "服务工作区", permissionPath: "/support" },
    { path: "/orders", label: organizationType === "supplier" ? "销售订单" : "采购订单", icon: "orders", section: "关联业务", permissionPath: "/orders" },
    { path: "/finance", label: organizationType === "supplier" ? "结算与提现" : "账户资金", icon: "finance", section: "关联业务", permissionPath: "/finance" },
  ];
}

function ticketActionNavigation(pathname: string, organizationType: string): NavItem[] {
  return [
    { path: "/support", label: organizationType === "operator" ? "返回工单中心" : "返回工单列表", icon: "support", section: "导航", permissionPath: "/support" },
    { path: pathname, label: "创建服务工单", icon: "support", section: "服务请求", permissionPath: "/support" },
    { path: "/orders", label: organizationType === "supplier" ? "销售订单" : "采购订单", icon: "orders", section: "关联业务", permissionPath: "/orders" },
    { path: "/finance", label: organizationType === "supplier" ? "结算与提现" : "账户资金", icon: "finance", section: "关联业务", permissionPath: "/finance" },
  ];
}

function integrationTargetNavigation(pathname: string): NavItem[] {
  const targetMatch = pathname.match(/^\/integrations\/([^/]+)/);
  const targetID = targetMatch?.[1] && targetMatch[1] !== "new" ? targetMatch[1] : "";
  if (!targetID) {
    return [
      { path: "/integrations", label: "接收平台列表", icon: "integrations", section: "接收平台工作区", permissionPath: "/integrations" },
      { path: "/integrations/new", label: "新增接收平台", icon: "integrations", section: "接收平台工作区", permissionPath: "/integrations" },
      { path: "/orders", label: "交付任务", icon: "orders", section: "关联业务", permissionPath: "/orders" },
      { path: "/reports/collection-health", label: "采集健康", icon: "reports", section: "关联业务", permissionPath: "/reports" },
    ];
  }
  const detailPath = `/integrations/${targetID}`;
  return [
    { path: "/integrations", label: "返回接收平台", icon: "integrations", section: "导航", permissionPath: "/integrations" },
    { path: detailPath, label: "接收平台详情", icon: "dashboard", section: "接收平台工作区", permissionPath: "/integrations" },
    { path: `${detailPath}/edit`, label: "编辑账号凭据", icon: "security", section: "接收平台工作区", permissionPath: "/integrations" },
    { path: "/orders", label: "交付任务", icon: "orders", section: "关联业务", permissionPath: "/orders" },
    { path: "/reports/collection-health", label: "采集健康", icon: "reports", section: "关联业务", permissionPath: "/reports" },
  ];
}

function automationPolicyNavigation(pathname: string): NavItem[] {
  const policyMatch = pathname.match(/^\/automation\/([^/]+)/);
  const policyID = policyMatch?.[1] && policyMatch[1] !== "new" ? policyMatch[1] : "";
  if (!policyID) {
    return [
      { path: "/automation", label: "规则列表", icon: "automation", section: "规则工作区", permissionPath: "/automation" },
      { path: "/automation/new", label: "新建采购规则", icon: "automation", section: "规则工作区", permissionPath: "/automation" },
      { path: "/integrations", label: "接收平台", icon: "integrations", section: "关联配置", permissionPath: "/integrations" },
      { path: "/market", label: "采购市场", icon: "market", section: "关联配置", permissionPath: "/market" },
    ];
  }
  const detailPath = `/automation/${policyID}`;
  return [
    { path: "/automation", label: "返回规则列表", icon: "automation", section: "导航", permissionPath: "/automation" },
    { path: detailPath, label: "规则详情", icon: "dashboard", section: "规则工作区", permissionPath: "/automation" },
    { path: `${detailPath}/edit`, label: "编辑规则", icon: "automation", section: "规则工作区", permissionPath: "/automation" },
    { path: `${detailPath}/executions`, label: "执行记录", icon: "reports", section: "规则工作区", permissionPath: "/automation" },
    { path: "/integrations", label: "接收平台", icon: "integrations", section: "关联配置", permissionPath: "/integrations" },
    { path: "/market", label: "采购市场", icon: "market", section: "关联配置", permissionPath: "/market" },
  ];
}

function persistentNavigationChildren(item: NavItem, subject: Subject): NavItem[] {
  const children = item.path === "/operations/platforms" ? operationsNavigation
    : item.path === "/catalog/providers" ? catalogNavigation
    : item.path === "/developer/imports" ? developerNavigation
    : item.path === "/integrations" ? integrationTargetNavigation("/integrations").filter((child) => child.path.startsWith("/integrations"))
    : item.path === "/automation" ? automationPolicyNavigation("/automation").filter((child) => child.path.startsWith("/automation"))
    : item.path === "/reports" ? reportsNavigation[subject.organization_type]
    : item.path === "/finance" ? financeNavigation[subject.organization_type]
    : item.path === "/system" ? systemNavigation
    : item.path === "/security" ? securityNavigation(subject)
    : [];
  return children
    .filter((child) => child.path !== "/catalog/pricing" || subject.roles.includes("admin"))
    .filter((child) => canAccessPath(subject, child.permissionPath ?? child.path));
}

export function Shell({ pathname, children }: { pathname: string; children: ReactNode }) {
  const { subject, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const notifications = useResource<ListResponse<JSONObject>>("/api/v1/notifications?unread_only=true&limit=50", { items: [] });
  const profile = useResource<JSONObject>("/api/v1/me/profile", {});
  useEffect(() => {
    setMenuOpen(false);
    setAccountMenuOpen(false);
    setSearchOpen(false);
  }, [pathname]);
  useEffect(() => {
    const refreshProfile = () => void profile.refresh();
    const closeAccountMenu = (event: PointerEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) setAccountMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAccountMenuOpen(false);
    };
    window.addEventListener("keymarket:profile", refreshProfile);
    window.addEventListener("pointerdown", closeAccountMenu);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keymarket:profile", refreshProfile);
      window.removeEventListener("pointerdown", closeAccountMenu);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, []);
  if (!subject) return null;
  const contextualItems = subject.organization_type === "platform" && pathname.startsWith("/integrations")
    ? integrationTargetNavigation(pathname)
    : subject.organization_type === "platform" && pathname.startsWith("/automation")
    ? automationPolicyNavigation(pathname)
    : subject.organization_type === "platform" && pathname.startsWith("/market/")
    ? marketplaceListingNavigation(pathname)
    : pathname.startsWith("/inquiries/")
    ? inquiryActionNavigation(pathname, subject.organization_type)
    : pathname.startsWith("/orders/")
    ? orderDetailNavigation(pathname, subject.organization_type, subject.roles)
    : pathname.startsWith("/operations/deliveries/")
    ? operatorDeliveryDetailNavigation(pathname)
    : pathname === "/support/new"
    ? ticketActionNavigation(pathname, subject.organization_type)
    : pathname.startsWith("/support/")
    ? ticketDetailNavigation(pathname, subject.organization_type)
    : pathname === "/reports/reconciliations/new" || /^\/reports\/reconciliations\/[^/]+\/differences\/[^/]+\/resolve$/.test(pathname)
    ? reconciliationActionNavigation(pathname)
    : pathname.startsWith("/reports/reconciliations/")
    ? reconciliationDetailNavigation(pathname)
    : pathname.startsWith("/reports")
    ? reportsNavigation[subject.organization_type]
    : pathname === "/credit/appeal"
    ? creditActionNavigation()
    : /^\/security\/(password|mfa\/(setup|disable)|invitations\/new|members\/[^/]+\/edit)$/.test(pathname)
    ? securityActionNavigation(pathname, subject)
    : pathname.startsWith("/security")
    ? securityNavigation(subject)
    : subject.organization_type === "supplier" && pathname.startsWith("/inventory/")
    ? supplierListingNavigation(pathname)
    : subject.organization_type === "supplier" && /^\/developer\/(imports|clients|webhooks)\/new$/.test(pathname)
      ? developerActionNavigation(pathname)
    : subject.organization_type === "supplier" && pathname.startsWith("/developer")
      ? developerNavigation
    : subject.organization_type === "operator" && (pathname === "/operations/platforms/new" || pathname === "/operations/suppliers/new")
      ? operationsActionNavigation(pathname)
    : subject.organization_type === "operator" && /^\/operations\/(platforms|suppliers)\/[^/]+$/.test(pathname)
      ? customerProfileNavigation(pathname)
    : subject.organization_type === "operator" && /^\/system\/(?:users\/[^/]+\/status|organizations\/[^/]+\/(?:status|credit)|operators\/(?:new|[^/]+\/edit))$/.test(pathname)
      ? systemActionNavigation(pathname)
    : subject.organization_type === "operator" && pathname.startsWith("/system")
      ? systemNavigation
    : subject.organization_type === "operator" && /^\/catalog\/(providers|credential-types|models|pricing)\/new$/.test(pathname)
      ? catalogActionNavigation(pathname)
    : subject.organization_type === "operator" && pathname.startsWith("/catalog/")
      ? catalogNavigation.filter((item) => item.path !== "/catalog/pricing" || subject.roles.includes("admin"))
    : subject.organization_type === "operator" && (
      pathname.startsWith("/operations/managed-platforms/") ||
      pathname.startsWith("/operations/assignments/") ||
      pathname.startsWith("/operations/team/") ||
      pathname.startsWith("/operations/reviews/")
    )
      ? operationsActionNavigation(pathname)
    : subject.organization_type === "operator" && pathname.startsWith("/operations")
      ? operationsNavigation
    : /^\/finance\/(topups\/[^/]+\/(refund|cancel)|refunds\/[^/]+\/(complete|reject)|adjustments\/[^/]+\/(approve|reject)|withdrawals\/[^/]+\/payout|commissions\/[^/]+\/payout|invoices\/[^/]+\/process)$/.test(pathname)
        ? financeProcessNavigation(pathname)
    : /^\/finance\/(topups|accounts|withdrawals|adjustments|commissions|invoices)\/new$/.test(pathname)
        ? financeActionNavigation(pathname, subject.organization_type)
    : pathname.startsWith("/finance")
        ? financeNavigation[subject.organization_type]
        : [];
  const primaryItems = primaryNavigation(subject)
    .filter((item) => canAccessPath(subject, item.permissionPath ?? item.path));
  const persistentSecondaryItems = primaryItems.flatMap((item) => persistentNavigationChildren(item, subject));
  const primaryRoot = (item: NavItem) => item.path === "/operations/platforms" ? "/operations"
    : item.path === "/catalog/providers" ? "/catalog"
      : item.path === "/developer/imports" ? "/developer"
        : item.path;
  const activePrimary = primaryItems
    .filter((item) => pathname === item.path || pathname.startsWith(`${primaryRoot(item)}/`))
    .sort((left, right) => primaryRoot(right).length - primaryRoot(left).length)[0] ?? primaryItems[0];
  const secondaryItems = contextualItems
    .filter((item) => !item.label.startsWith("返回"))
    .filter((item) => canAccessPath(subject, item.permissionPath ?? item.path));
  const items = [...primaryItems, ...persistentSecondaryItems, ...secondaryItems]
    .filter((item, index, candidates) => candidates.findIndex((candidate) => candidate.path === item.path && candidate.label === item.label) === index);
  const activeItem = contextualItems.find((item) => item.path === pathname)
    ?? primaryItems.find((item) => item.path === pathname)
    ?? activePrimary;
  const roleLabel = subject.organization_type === "platform" ? "平台方"
    : subject.organization_type === "supplier" ? "供应商"
      : subject.roles.includes("admin") ? "管理员"
        : subject.roles.includes("manager") ? "管理"
          : "运营";
	const operatorMFALocked = subject.organization_type === "operator" &&
		(subject.roles.includes("admin") || subject.roles.includes("manager")) && !subject.mfa_confirmed;
  const signOut = async () => {
    await logout();
    setMenuOpen(false);
    setAccountMenuOpen(false);
    navigate("/login");
  };
  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, []);

  return <div className="console-shell">
    <header className="topbar">
      <div className="topbar-location" aria-label="当前位置">
        <button className="menu-button" onClick={() => setMenuOpen(true)} aria-label="打开导航"><Icon name="menu" /></button>
        <span>{activeItem?.section ?? "工作台"}</span><i>/</i><strong>{activeItem?.label ?? "经营概览"}</strong>
      </div>
      <div className="topbar-actions">
        <button className="search-button" onClick={() => setSearchOpen(true)} aria-label="搜索工作区"><Icon name="search" /><span>搜索</span><kbd>⌘ K</kbd></button>
        <button className="notification-button" onClick={() => setNotificationsOpen(!notificationsOpen)} aria-label="通知" title={`${notifications.data.items.length} 条未读`}><Icon name="bell" />{notifications.data.items.length > 0 && <span />}</button>
        <div className="account-menu-wrap" ref={accountMenuRef}>
          <button className="avatar" type="button" aria-label="打开账号菜单" aria-haspopup="menu" aria-expanded={accountMenuOpen} onClick={() => setAccountMenuOpen((open) => !open)}>
            {profile.data.avatar_data_url
              ? <img src={String(profile.data.avatar_data_url)} alt="" />
              : String(profile.data.nickname || subject.email).slice(0, 2).toUpperCase()}
          </button>
          {accountMenuOpen && <div className="account-menu" role="menu" aria-label="账号菜单">
            <div className="account-menu-identity">
              <strong>{String(profile.data.nickname || "未设置昵称")}</strong>
              <span>{String(profile.data.email || subject.email)}</span>
            </div>
            <button role="menuitem" onClick={() => navigate("/account/profile")}><Icon name="security" /><span><strong>个人资料</strong><small>设置头像和昵称</small></span></button>
            <button role="menuitem" onClick={() => navigate("/account/email")}><Icon name="support" /><span><strong>修改邮箱</strong><small>更换登录邮箱</small></span></button>
            <button role="menuitem" onClick={() => navigate("/security/password")}><Icon name="security" /><span><strong>修改密码</strong><small>更新登录凭证</small></span></button>
            <button role="menuitem" className="account-menu-logout" onClick={() => void signOut()}><Icon name="close" /><span><strong>注销登录</strong><small>退出当前账号</small></span></button>
          </div>}
        </div>
      </div>
    </header>
    <aside className={`sidebar ${menuOpen ? "open" : ""}`}>
      <div className="sidebar-brand">
        <span className="brand-mark">KM</span>
        <div><strong>KEYMARKET</strong><small>企业 KEY 交易平台</small></div>
        <button className="mobile-close" aria-label="关闭导航" onClick={() => setMenuOpen(false)}><Icon name="close" /></button>
      </div>
      <div className="sidebar-workspace">
        <div><span>当前身份</span><strong>{roleLabel}</strong></div>
        <Badge>{subject.organization_status}</Badge>
      </div>
      <nav aria-label={`${roleLabel}主导航`}>
        {primaryItems.map((item) => {
          const children = persistentNavigationChildren(item, subject);
          const expanded = children.length > 0;
          const exact = item.path === pathname;
          const parentActive = pathname === item.path || pathname.startsWith(`${primaryRoot(item)}/`);
          const activeChild = children
            .filter((child) => pathname === child.path || pathname.startsWith(`${child.path}/`))
            .sort((left, right) => right.path.length - left.path.length)[0];
          return <div className="nav-entry" key={item.path}>
            <button
              aria-current={(!expanded && (exact || pathname.startsWith(`${item.path}/`))) || (expanded && parentActive && !activeChild) ? "page" : undefined}
              aria-expanded={expanded || undefined}
              className={expanded && parentActive ? "active-parent" : !expanded && (exact || pathname.startsWith(`${item.path}/`)) ? "active" : ""}
              onClick={() => { navigate(item.path); setMenuOpen(false); }}
            ><Icon name={item.icon} />{item.label}</button>
            {expanded ? <div className="nav-children" aria-label={`${item.label}子菜单`}>
              {children.map((child) => <button
                key={child.path}
                aria-current={child.path === activeChild?.path ? "page" : undefined}
                className={child.path === activeChild?.path ? "active" : ""}
                onClick={() => { navigate(child.path); setMenuOpen(false); }}
              ><Icon name={child.icon} />{child.label}</button>)}
            </div> : null}
          </div>;
        })}
      </nav>
      <div className="sidebar-foot">
        <div className="org-state"><span className={`state-dot ${subject.organization_status}`} /><div><strong>{subject.email}</strong><small>{subject.roles.map(formatDisplayValue).join(" / ")}</small></div></div>
      </div>
    </aside>
    {menuOpen && <button className="sidebar-scrim" aria-label="关闭导航遮罩" onClick={() => setMenuOpen(false)} />}
    <section className="workspace">
      {subject.organization_status !== "active" && <div className="status-banner"><strong>组织状态：{formatDisplayValue(subject.organization_status)}</strong><span>{subject.organization_type === "supplier" ? "能力证明正在自动检测并等待运营审核，审核前目录操作受限。" : "请完成邮箱验证或联系运营开通组织。"}</span></div>}
		{operatorMFALocked && <div className="status-banner mfa-lock-banner"><strong>敏感操作已锁定</strong><span>{subject.mfa_enabled ? "当前会话未完成多因素认证，请重新登录后完成二次验证。" : "管理员和管理角色必须先启用多因素认证，才能审核、调账或修改权限。"}</span><Button tone="quiet" onClick={() => subject.mfa_enabled ? void signOut() : navigate("/security/mfa/setup")}>{subject.mfa_enabled ? "重新登录验证" : "立即设置 MFA"}</Button></div>}
      <main className="content">{children}</main>
    </section>
    {notificationsOpen && <NotificationDrawer subject={subject} onClose={() => setNotificationsOpen(false)} onChanged={notifications.refresh} />}
    {searchOpen && <CommandPalette items={items} onClose={() => setSearchOpen(false)} />}
  </div>;
}

function CommandPalette({ items, onClose }: { items: NavItem[]; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const matches = items.filter((item) => `${item.section} ${item.label}`.toLowerCase().includes(query.trim().toLowerCase()));
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  return <div className="command-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><section className="command-palette" role="dialog" aria-modal="true" aria-label="搜索工作区"><label><Icon name="search" /><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索页面或功能" aria-label="搜索页面或功能" /><kbd>ESC</kbd></label><div>{matches.map((item) => <button key={`${item.path}:${item.label}`} aria-label={item.label} onClick={() => { navigate(item.path); onClose(); }}><span><Icon name={item.icon} />{item.label}</span><small>{item.section}</small></button>)}{!matches.length && <Empty detail="没有匹配的工作区" />}</div></section></div>;
}

function NotificationDrawer({ subject, onClose, onChanged }: { subject: Subject; onClose: () => void; onChanged: () => Promise<void> }) {
  const { data, loading, refresh } = useResource<ListResponse<JSONObject>>("/api/v1/notifications?limit=50", { items: [] });
  const markRead = async (id: string) => {
    await api(`/api/v1/notifications/${id}/read`, { method: "POST" });
    await Promise.all([refresh(), onChanged()]);
  };
  const openRelated = async (item: JSONObject) => {
    if (!item.read_at) await markRead(String(item.id));
    const path = notificationPath(String(item.related_type ?? ""), String(item.related_id ?? ""), subject);
    if (path) {
      onClose();
      navigate(path);
    }
  };
  return <div className="drawer-layer" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><aside className="notification-drawer"><header><div><p className="section-index">实时信箱</p><h2>通知</h2></div><button onClick={onClose} aria-label="关闭通知"><Icon name="close" /></button></header>{loading ? <Spinner /> : data.items.length ? <div className="notification-list">{data.items.map((item) => <article key={String(item.id)} className={item.read_at ? "read" : ""}><div><Badge>{String(item.related_type ?? "system")}</Badge><time>{new Date(String(item.created_at ?? "")).toLocaleString("zh-CN")}</time></div><h3>{String(item.title ?? "通知")}</h3><p>{String(item.body ?? "")}</p><div className="panel-actions">{notificationPath(String(item.related_type ?? ""), String(item.related_id ?? ""), subject) && <Button onClick={() => void openRelated(item)}>查看关联业务</Button>}{!item.read_at && <Button tone="quiet" onClick={() => void markRead(String(item.id))}>标记已读</Button>}</div></article>)}</div> : <Empty detail="没有新的系统、交易或工单通知" />}</aside></div>;
}

function notificationPath(relatedType: string, relatedID: string, subject: Subject) {
  let path = "";
  let permissionPath = "";
  if (relatedType === "support_ticket") {
    path = relatedID ? `/support/${encodeURIComponent(relatedID)}` : "/support";
    permissionPath = "/support";
  }
  if (relatedType === "order") {
    path = relatedID ? `/orders/${encodeURIComponent(relatedID)}` : "/orders";
    permissionPath = "/orders";
  }
  if (["payment", "refund", "withdrawal", "invoice", "settlement"].includes(relatedType)) path = permissionPath = "/finance";
  if (relatedType === "target") path = permissionPath = "/integrations";
  if (relatedType === "credit_profile") path = permissionPath = "/credit";
  if (["listing", "credential"].includes(relatedType)) path = permissionPath = subject.organization_type === "supplier" ? "/inventory" : "/market";
  return path && canAccessPath(subject, permissionPath || path) ? path : "";
}
